import * as _ from 'lodash';

import {type RunHistoryKeyInfo, type RunHistoryRow} from '../../../types/run';
import {timestampAfter, timestampBefore} from '../../../util/compare';
import {type DomainMaybe} from '../../vis/types';

const MIN_STEP_EXTRA_MARGIN = 51;
const MAX_STEP_EXTRA_MARGIN = 2;

export function inRange(
  v: number,
  keyIsTimestamp: boolean,
  xMin?: number,
  xMax?: number
) {
  if (xMin != null && lessThan(v, xMin, keyIsTimestamp)) {
    return false;
  }
  if (xMax != null && greaterThan(v, xMax, keyIsTimestamp)) {
    return false;
  }
  return true;
}

function lessThan(a: number, b: number, keyIsTimestamp: boolean) {
  if (keyIsTimestamp) {
    return timestampBefore(a, b);
  }
  return a < b;
}

function greaterThan(a: number, b: number, keyIsTimestamp: boolean) {
  if (keyIsTimestamp) {
    return timestampAfter(a, b);
  }
  return a > b;
}

type RunData = {
  displayName: string;
  history: RunHistoryRow[];
  keyInfo: RunHistoryKeyInfo;
  name: string;
};
// TODO(axel): Move this re-sampling behavior to the backend.
// May have to consider how it interacts with `runs-low` to avoid breaking anything.
export function calcStepRange(
  data: RunData[],
  historyKeyInfo?: RunHistoryKeyInfo,
  xAxis?: string,
  xMin?: number,
  xMax?: number,
  xExpression?: string
): DomainMaybe {
  const key = xAxis;
  const keyIsStep = key === '_step';
  const keyIsTimestamp = key === '_timestamp';
  if (key == null) {
    return [null, null];
  }

  /**
   * Note: it looks like we disable this when there's an xExpression. I assume this is because computing the x-axis values through the expression breaks the zooming / querying code. The old check was a loose equality check `!=` to `null` so `""` was still tripping the eject here. You can get an empty string by adding an xExpression and then deleting it in the panel config.
   */
  // eslint-disable-next-line no-extra-boolean-cast
  if (!!xExpression) {
    return [null, null];
  }
  // We only re-sample when x-axis is monotonically increasing
  if (!historyKeyInfo?.keys[key]?.monotonic) {
    return [null, null];
  }

  /**
   * Note: the sampled history query turns a multi-metric history like: history: [...[aPoints], ...[bPoints], ...]. The bucketed history query returns them as [ [aPoints], [bPoints], [cPoints] ] so that we don't have to loop the entire history to build a line for each metric. As a consequence, we need to flatten the history here to hook into the legacy range calculations since it operates across all the metrics.
   */
  const isBucketedData = data.some(d => Array.isArray(d.history[0]));
  /**
   * Bucketed data doesn't have points which match the key exactly, they have `-Avg` onto the metric name
   */
  const modKey = isBucketedData ? `${key}Avg` : key;
  const modStep = isBucketedData ? '_stepAvg' : '_step';
  const safeData: RunData[] = isBucketedData
    ? data.map(d => {
        return {
          ...d,
          history: d.history.flat(),
        } as RunData;
      })
    : data;
  let xMins: number[] = [];
  let xMaxs: number[] = [];

  if (xMin != null) {
    // Find the largest `_step` value with x-axis value less than the allowed range
    xMins = safeData
      .map(({history}) => {
        let largestStepValue: number = -Infinity;

        for (const runHistoryRow of history) {
          if (!_.isNumber(runHistoryRow[modKey])) {
            continue;
          }

          if (greaterThan(runHistoryRow[modKey], xMin, keyIsTimestamp)) {
            // We've crossed into the user-defined range, and every x-axis value after this one will be bigger than xMin

            if (
              !keyIsStep &&
              inRange(runHistoryRow[modKey], keyIsTimestamp) &&
              _.isNumber(runHistoryRow[modStep]) &&
              largestStepValue === -Infinity
            ) {
              // If we haven't encountered any other `_step` values, pick this one
              largestStepValue = runHistoryRow[modStep];
            }

            break;
          }

          if (!_.isNumber(runHistoryRow[modStep])) {
            continue;
          }

          largestStepValue = runHistoryRow[modStep];
        }

        return largestStepValue;
      })
      .filter(_.isFinite);
  }
  if (xMax != null) {
    // Find the smallest `_step` value with x-axis value greater than the allowed range
    xMaxs = safeData
      .map(({history}) => {
        let smallestStepValue: number = Infinity;

        for (let i = history.length - 1; i >= 0; i--) {
          const runHistoryRow = history[i];
          if (!_.isNumber(runHistoryRow[modKey])) {
            continue;
          }

          if (lessThan(runHistoryRow[modKey], xMax, keyIsTimestamp)) {
            // We've crossed into the user-defined range, and every x-axis value before this one will be smaller than xMax

            if (
              !keyIsStep &&
              inRange(runHistoryRow[modKey], keyIsTimestamp) &&
              _.isNumber(runHistoryRow[modStep]) &&
              smallestStepValue === Infinity
            ) {
              // If we haven't encountered any other `_step` values, pick this one
              smallestStepValue = runHistoryRow[modStep];
            }

            break;
          }

          if (!_.isNumber(runHistoryRow[modStep])) {
            continue;
          }

          smallestStepValue = runHistoryRow[modStep];
        }

        return smallestStepValue;
      })
      .filter(_.isFinite);
  }

  let minStep: number | null;
  let maxStep: number | null;
  if (keyIsStep) {
    // if key is step we can choose the narrowest range
    maxStep = _.min(xMaxs) ?? null;
    minStep = _.max(xMins) ?? null;
  } else {
    // since we don't know if the steps corresponding to the xranges overlap
    // we need to choose the widest range
    maxStep = _.max(xMaxs) ?? null;
    minStep = _.min(xMins) ?? null;
  }
  if (minStep != null) {
    minStep -= MIN_STEP_EXTRA_MARGIN;
  }
  if (maxStep != null) {
    maxStep += MAX_STEP_EXTRA_MARGIN;
  }

  return [minStep, maxStep];
}
